import { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import { Portal } from '@mantine/core';
import {
  ToastAlert,
  ToastOptions,
  ToastPromiseOptions,
  ModalAlert,
  ModalAlertOptions,
  ModalAlertPromptOptions,
  BannerAlert as BannerAlertType,
  BannerAlertOptions,
  ToastAPI,
  AlertAPI,
  BannerAPI,
  AlertType,
  ModalAlertType,
} from './alert-types';
import ToastContainer from '../feedback/ToastContainer';
import AlertModal from '../overlays/AlertModal';
import BannerAlert from '../feedback/BannerAlert';

// ============================================================================
// Context Definition
// ============================================================================

interface AlertContextValue {
  toasts: ToastAlert[];
  modalAlert: ModalAlert | null;
  bannerAlert: BannerAlertType | null;
  addToast: (toast: Omit<ToastAlert, 'id'>) => string;
  removeToast: (id: string) => void;
  clearToasts: () => void;
  showModalAlert: (alert: Omit<ModalAlert, 'id'>) => Promise<any>;
  dismissModalAlert: () => void;
  showBannerAlert: (alert: Omit<BannerAlertType, 'id'>) => string;
  dismissBannerAlert: (id?: string) => void;
}

const AlertContext = createContext<AlertContextValue | undefined>(undefined);

// ============================================================================
// Provider Props
// ============================================================================

export interface AlertProviderProps {
  children: ReactNode;
  maxToasts?: number;
  defaultToastDuration?: number;
  defaultToastPosition?: ToastAlert['position'];
}

// ============================================================================
// Provider Component
// ============================================================================

export function AlertProvider({
  children,
  maxToasts = 5,
  defaultToastDuration = 5000,
  defaultToastPosition = 'top-right',
}: AlertProviderProps) {
  const [toasts, setToasts] = useState<ToastAlert[]>([]);
  const [modalAlert, setModalAlert] = useState<ModalAlert | null>(null);
  const [bannerAlert, setBannerAlert] = useState<BannerAlertType | null>(null);
  const [modalPromise, setModalPromise] = useState<{
    resolve: (value: any) => void;
    reject: (reason?: any) => void;
  } | null>(null);

  // ========================================
  // Toast Management
  // ========================================

  const addToast = useCallback(
    (toast: Omit<ToastAlert, 'id'>) => {
      const id = Math.random().toString(36).substring(2, 11);
      const newToast: ToastAlert = {
        id,
        ...toast,
        position: toast.position ?? defaultToastPosition,
        duration: toast.duration ?? defaultToastDuration,
      };

      setToasts((prev) => {
        // Limit toasts per position
        const positionToasts = prev.filter((t) => t.position === newToast.position);
        if (positionToasts.length >= maxToasts) {
          // Remove oldest toast at this position
          const toRemove = positionToasts[0];
          return [...prev.filter((t) => t.id !== toRemove.id), newToast];
        }
        return [...prev, newToast];
      });

      // Auto-dismiss if duration > 0
      if (newToast.duration && newToast.duration > 0) {
        setTimeout(() => {
          removeToast(id);
        }, newToast.duration);
      }

      return id;
    },
    [defaultToastPosition, defaultToastDuration, maxToasts]
  );

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const clearToasts = useCallback(() => {
    setToasts([]);
  }, []);

  // ========================================
  // Modal Alert Management
  // ========================================

  const showModalAlert = useCallback((alert: Omit<ModalAlert, 'id'>) => {
    return new Promise((resolve, reject) => {
      const id = Math.random().toString(36).substring(2, 11);
      const newAlert: ModalAlert = {
        id,
        ...alert,
      };

      setModalAlert(newAlert);
      setModalPromise({ resolve, reject });
    });
  }, []);

  const dismissModalAlert = useCallback(() => {
    setModalAlert(null);
    setModalPromise(null);
  }, []);

  const resolveModalAlert = useCallback(
    (value: any) => {
      if (modalPromise) {
        modalPromise.resolve(value);
      }
      dismissModalAlert();
    },
    [modalPromise, dismissModalAlert]
  );

  const rejectModalAlert = useCallback(() => {
    if (modalPromise) {
      modalPromise.resolve(null); // Don't reject, just resolve with null
    }
    dismissModalAlert();
  }, [modalPromise, dismissModalAlert]);

  // ========================================
  // Banner Alert Management
  // ========================================

  const showBannerAlert = useCallback((alert: Omit<BannerAlertType, 'id'>) => {
    const id = Math.random().toString(36).substring(2, 11);
    const newAlert: BannerAlertType = {
      id,
      ...alert,
      position: alert.position || 'top',
    };

    setBannerAlert(newAlert);

    // Auto-dismiss if not persistent and has duration
    if (!newAlert.persistent && newAlert.duration) {
      setTimeout(() => {
        dismissBannerAlert(id);
      }, newAlert.duration);
    }

    return id;
  }, []);

  const dismissBannerAlert = useCallback((id?: string) => {
    setBannerAlert((prev) => {
      if (!prev) return null;
      if (!id || prev.id === id) return null;
      return prev;
    });
  }, []);

  // ========================================
  // Context Value
  // ========================================

  const contextValue = useMemo(
    () => ({
      toasts,
      modalAlert,
      bannerAlert,
      addToast,
      removeToast,
      clearToasts,
      showModalAlert,
      dismissModalAlert,
      showBannerAlert,
      dismissBannerAlert,
    }),
    [
      toasts,
      modalAlert,
      bannerAlert,
      addToast,
      removeToast,
      clearToasts,
      showModalAlert,
      dismissModalAlert,
      showBannerAlert,
      dismissBannerAlert,
    ]
  );

  return (
    <AlertContext.Provider value={contextValue}>
      <AlertContextSetter />
      {children}
      <ToastContainer toasts={toasts} onDismiss={removeToast} />
      {modalAlert && (
        <Portal>
          <AlertModal alert={modalAlert} onResolve={resolveModalAlert} onReject={rejectModalAlert} />
        </Portal>
      )}
      {bannerAlert && <BannerAlert alert={bannerAlert} onDismiss={dismissBannerAlert} />}
    </AlertContext.Provider>
  );
}

// Helper component to set context instance for imperative API
function AlertContextSetter() {
  const context = useAlertContext();

  // Set the context instance on mount
  if (context) {
    setAlertContextInstance(context);
  }

  return null;
}

// ============================================================================
// Custom Hook
// ============================================================================

export function useAlertContext() {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlertContext must be used within AlertProvider');
  }
  return context;
}

// ============================================================================
// Helper Object: Toast API
// ============================================================================

let alertContextInstance: AlertContextValue | null = null;

// This is a workaround to access context outside components
// Will be set by a helper component inside the provider
export function setAlertContextInstance(instance: AlertContextValue) {
  alertContextInstance = instance;
}

function getAlertContext(): AlertContextValue {
  if (!alertContextInstance) {
    throw new Error('Alert system not initialized. Make sure AlertProvider is mounted.');
  }
  return alertContextInstance;
}

export const toast: ToastAPI = {
  success: (title: string, message?: string, options?: ToastOptions) => {
    const ctx = getAlertContext();
    return ctx.addToast({
      type: 'success',
      title,
      message,
      position: options?.position || 'top-right',
      ...options,
    });
  },

  error: (title: string, message?: string, options?: ToastOptions) => {
    const ctx = getAlertContext();
    return ctx.addToast({
      type: 'error',
      title,
      message,
      position: options?.position || 'top-right',
      ...options,
    });
  },

  warning: (title: string, message?: string, options?: ToastOptions) => {
    const ctx = getAlertContext();
    return ctx.addToast({
      type: 'warning',
      title,
      message,
      position: options?.position || 'top-right',
      ...options,
    });
  },

  info: (title: string, message?: string, options?: ToastOptions) => {
    const ctx = getAlertContext();
    return ctx.addToast({
      type: 'info',
      title,
      message,
      position: options?.position || 'top-right',
      ...options,
    });
  },

  custom: (options: Omit<ToastAlert, 'id'>) => {
    const ctx = getAlertContext();
    return ctx.addToast(options);
  },

  promise: async <T,>(promise: Promise<T>, options: ToastPromiseOptions<T>): Promise<T> => {
    const ctx = getAlertContext();
    const loadingId = ctx.addToast({
      type: 'info',
      title: options.loading,
      duration: 0, // Don't auto-dismiss
      position: options.position || 'top-right',
    });

    try {
      const result = await promise;
      ctx.removeToast(loadingId);

      const successMessage = typeof options.success === 'function' ? options.success(result) : options.success;
      ctx.addToast({
        type: 'success',
        title: successMessage,
        position: options.position || 'top-right',
        duration: options.duration,
      });

      return result;
    } catch (error) {
      ctx.removeToast(loadingId);

      const errorMessage =
        typeof options.error === 'function' ? options.error(error as Error) : options.error;
      ctx.addToast({
        type: 'error',
        title: errorMessage,
        position: options.position || 'top-right',
        duration: options.duration,
      });

      throw error;
    }
  },

  dismiss: (id: string) => {
    const ctx = getAlertContext();
    ctx.removeToast(id);
  },

  dismissAll: () => {
    const ctx = getAlertContext();
    ctx.clearToasts();
  },
};

// ============================================================================
// Helper Object: Alert API (Modal)
// ============================================================================

export const alert: AlertAPI = {
  success: async (title: string, message: string, options?: ModalAlertOptions) => {
    const ctx = getAlertContext();
    await ctx.showModalAlert({
      type: 'success',
      title,
      message,
      confirmText: options?.confirmText || 'OK',
      ...options,
    });
  },

  error: async (title: string, message: string, options?: ModalAlertOptions) => {
    const ctx = getAlertContext();
    await ctx.showModalAlert({
      type: 'error',
      title,
      message,
      confirmText: options?.confirmText || 'OK',
      ...options,
    });
  },

  warning: async (title: string, message: string, options?: ModalAlertOptions) => {
    const ctx = getAlertContext();
    await ctx.showModalAlert({
      type: 'warning',
      title,
      message,
      confirmText: options?.confirmText || 'OK',
      ...options,
    });
  },

  info: async (title: string, message: string, options?: ModalAlertOptions) => {
    const ctx = getAlertContext();
    await ctx.showModalAlert({
      type: 'info',
      title,
      message,
      confirmText: options?.confirmText || 'OK',
      ...options,
    });
  },

  confirm: async (title: string, message: string, options?: ModalAlertOptions): Promise<boolean> => {
    const ctx = getAlertContext();
    const result = await ctx.showModalAlert({
      type: 'confirm',
      title,
      message,
      confirmText: options?.confirmText || 'Confirm',
      cancelText: options?.cancelText || 'Cancel',
      ...options,
    });
    return result === true;
  },

  prompt: async (title: string, message: string, options?: ModalAlertPromptOptions): Promise<string | null> => {
    const ctx = getAlertContext();
    const result = await ctx.showModalAlert({
      type: 'prompt',
      title,
      message,
      confirmText: options?.confirmText || 'Submit',
      cancelText: options?.cancelText || 'Cancel',
      ...options,
    });
    return result || null;
  },

  custom: async (options: Omit<ModalAlert, 'id'>): Promise<any> => {
    const ctx = getAlertContext();
    return await ctx.showModalAlert(options);
  },
};

// ============================================================================
// Helper Object: Banner API
// ============================================================================

export const banner: BannerAPI = {
  success: (message: string, options?: BannerAlertOptions) => {
    const ctx = getAlertContext();
    return ctx.showBannerAlert({
      type: 'success',
      message,
      position: options?.position || 'top',
      ...options,
    });
  },

  error: (message: string, options?: BannerAlertOptions) => {
    const ctx = getAlertContext();
    return ctx.showBannerAlert({
      type: 'error',
      message,
      position: options?.position || 'top',
      ...options,
    });
  },

  warning: (message: string, options?: BannerAlertOptions) => {
    const ctx = getAlertContext();
    return ctx.showBannerAlert({
      type: 'warning',
      message,
      position: options?.position || 'top',
      ...options,
    });
  },

  info: (message: string, options?: BannerAlertOptions) => {
    const ctx = getAlertContext();
    return ctx.showBannerAlert({
      type: 'info',
      message,
      position: options?.position || 'top',
      ...options,
    });
  },

  dismiss: (id?: string) => {
    const ctx = getAlertContext();
    ctx.dismissBannerAlert(id);
  },
};

export default AlertProvider;
